import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useContent } from '../hooks/useContent';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { ModeType } from '../contexts/ModeContext';

interface BottomNavigationProps {
  activeTab: 'home' | 'inventory' | 'groups' | 'profile' | 'market';
  onTabChange: (tab: 'home' | 'inventory' | 'groups' | 'profile' | 'market') => void;
  activeMode?: ModeType;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ activeTab, onTabChange, activeMode = 'home' }) => {
  const { home, inventory, profile, common } = useContent();
  const { user } = useAuth();
  const { hasSubscriptionIssue } = useSubscription();
  const hasSubscriptionIssueFlag = hasSubscriptionIssue();
  // Ensure a portal container exists at the document body level so the nav
  // is never clipped by any scrolling/overflow containers.
  const portalContainer = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const existing = document.getElementById('bottom-nav-portal');
    if (existing) return existing;
    const el = document.createElement('div');
    el.id = 'bottom-nav-portal';
    // Keep this container at the top-level to bypass clipping contexts
    // Styling is handled by .bottom-navigation itself
    document.body.appendChild(el);
    return el;
  }, []);

  // Clean up the portal container if we created it (only if it has no other children)
  useEffect(() => {
    return () => {
      if (portalContainer && portalContainer.id === 'bottom-nav-portal') {
        // Only remove if empty to avoid removing a shared container
        if (portalContainer.childNodes.length === 0 && portalContainer.parentNode) {
          portalContainer.parentNode.removeChild(portalContainer);
        }
      }
    };
  }, [portalContainer]);
  const tabs = [
    {
      id: 'home' as const,
      label: home.title,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      id: 'groups' as const,
      label: 'People',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      id: 'inventory' as const,
      label: activeMode === 'kitchen' ? 'Grocery' : inventory.title,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M 20.5 12 A 8.5 8.5 0 0 1 5.1 17.7" stroke="#3B82F6" />
          <path d="M 3.5 12 A 8.5 8.5 0 0 1 18.9 6.3" stroke="#EAB308" />
          <polyline points="19 2 19 7 14 7" stroke="#EAB308" />
          <polyline points="5 22 5 17 10 17" stroke="#3B82F6" />
        </svg>
      )
    },
    {
      id: 'market' as const,
      label: activeMode === 'kitchen' ? 'Recipes' : activeMode === 'professional' ? 'Moving' : 'Market',
      icon: activeMode === 'kitchen' ? (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
          <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
        </svg>
      ) : activeMode === 'professional' ? (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="3" width="15" height="13" rx="1" />
          <path d="M16 8h5l2 5v3h-7V8z" />
          <circle cx="5.5" cy="18.5" r="2.5" />
          <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l1.5-5h15L21 9" />
          <path d="M3 9c0 1.5 1.5 3 3 3s3-1.5 3-3" />
          <path d="M9 9c0 1.5 1.5 3 3 3s3-1.5 3-3" />
          <path d="M15 9c0 1.5 1.5 3 3 3s3-1.5 3-3" />
          <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7" />
          <path d="M9 20v-5h6v5" />
        </svg>
      )
    },
    {
      id: 'profile' as const,
      label: (() => { const name = user?.display_name ? user.display_name.split(' ')[0] : (user?.role === 'admin' ? 'Admin' : profile.title); return name.length > 12 ? name.slice(0, 12) + '...' : name; })(),
      hasIssue: hasSubscriptionIssueFlag,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    }
  ];

  const nav = (
    <div className="bottom-navigation">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`bottom-nav-item ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'profile' && (tab as any).hasIssue ? 'bottom-nav-alert' : ''}`}
        >
          <div className="bottom-nav-icon">
            {tab.icon}
          </div>
          <span className="bottom-nav-label">{tab.label}</span>
        </button>
      ))}
    </div>
  );

  // Fallback to normal render if portal isn't available (SSR or very old env)
  if (!portalContainer) return nav;
  return ReactDOM.createPortal(nav, portalContainer);
};

export default BottomNavigation;
